import { FC, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import {
  InlineDropdownTableRowCell,
  InlineDropdownOption,
} from '../InlineDropdownTableRowCell/InlineDropdownTableRowCell';
import { BaseCellProps } from '../types';
import { REQUEST_STATUSES } from 'utils/constants/request';
import { RequestStatus } from 'utils/types/request';
import type { Request } from 'utils/customTypes';
import RequestStatusTag from 'Atoms/RequestStatusTag/RequestStatusTag';
import RequestStatusModals from 'Pages/RequestPage/components/TopBar/RequestStatusMenu/RequestStatusModals/RequestStatusModals';
import type { RequestStatusModalParams } from 'state/normalized/requests/requestsSlice';
import ConfirmCancellationModal, {
  type ConfirmCancellationRequestData,
} from 'Pages/RequestPage/components/ConfirmCancellationModal';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { selectUserId, isUserOrganizationAdmin } from 'state/User/userSlice';
import { requestPermissionsSelectors } from 'state/normalized/requestPermissions/requestPermissionsSlice';
import { RequestPermissions } from 'types/store/normalized/requestPermissions';

interface RequestStatusTableRowCellProps extends Omit<BaseCellProps, 'value'> {
  value: RequestStatus;
  canEdit?: boolean;
  isDisabled?: boolean;
  disabledMessage?: string;
  className?: string;
  /**
   * Request data for the cancelation flow (required when moving to canceled
   * from pending_cancellation) and for Completed-option gating — Completed is
   * only offered when isJsonSchema is true (CGP-240).
   */
  request?: ConfirmCancellationRequestData & Pick<Request, 'isJsonSchema'>;
  /** Called with new status and modal form params (comments, conditions, etc.) when user confirms */
  onSave?: (
    newStatus: string,
    params?: RequestStatusModalParams
  ) => void | Promise<void>;
}

export const getRequestStatusOptions = (
  userId?: string,
  request?: { ownerIds?: string[] } & Pick<Request, 'isJsonSchema'>,
  requestPermissions?: RequestPermissions,
  value?: RequestStatus,
  isAdmin?: boolean,
  isCompletedStatusEnabled?: boolean
): InlineDropdownOption[] => {
  // CGP-235: pending_page_approval is a system-driven gate. It is read-only on
  // the frontend — no inline transitions are offered from it (server enforces;
  // see CGP-124), so the inline editor has nothing to switch to.
  if (value === REQUEST_STATUSES.PENDING_PAGE_APPROVAL) {
    return [];
  }
  const requestStatusesArray: RequestStatus[] = [];
  Object.values(REQUEST_STATUSES).forEach((status) => {
    // CGP-180: Completed is selectable only for Approved requests, and only
    // when the advanced-requests-completed-status flag is on. It is never an
    // option for any other current status (and not for a Completed request
    // itself — from Completed the user picks a different destination status).
    // CGP-240: Completed is an advanced-requests-only status, so legacy
    // requests (isJsonSchema falsy) never get the option (server enforces
    // too — validateCompletedStatusTransition rejects legacy → Completed).
    if (status === REQUEST_STATUSES.COMPLETED) {
      if (
        isCompletedStatusEnabled &&
        value === REQUEST_STATUSES.APPROVED &&
        request?.isJsonSchema === true
      ) {
        requestStatusesArray.push(status);
      }
      return;
    }
    if (value === REQUEST_STATUSES.PENDING_CANCELLATION) {
      if (status !== REQUEST_STATUSES.CANCELED) return;
    } else if (
      status === REQUEST_STATUSES.PENDING_CANCELLATION ||
      status === REQUEST_STATUSES.DRAFT ||
      status === REQUEST_STATUSES.CANCELED ||
      status === REQUEST_STATUSES.PENDING_PAGE_APPROVAL
    ) {
      return;
    }
    requestStatusesArray.push(status);
  });
  return requestStatusesArray.map((status) => {
    const isOwner = request?.ownerIds?.includes(userId ?? '');
    const disabled =
      value !== status &&
      ((status === REQUEST_STATUSES.REJECTED &&
        !!userId &&
        !isOwner &&
        !isAdmin) ||
        (status !== REQUEST_STATUSES.REJECTED &&
          requestPermissions?.canEditStatusInline !== true &&
          !isAdmin));
    return {
      value: status,
      label: intl.get(
        `REQUESTS_LIST_PAGE.TABLE.STATUS_LABEL.${status.toUpperCase()}`
      ),
      disabled,
    };
  });
};

const customizedValue = (value: string) => {
  return <RequestStatusTag status={value as RequestStatus} />;
};

export const RequestStatusTableRowCell: FC<RequestStatusTableRowCellProps> = ({
  value,
  isEditable,
  className = '',
  onSave,
  onCancel,
  canEdit = true,
  isDisabled = false,
  disabledMessage,
  request,
}) => {
  const requestPermissions = useSelector((state: RootState) =>
    requestPermissionsSelectors.selectById(state, request?.id || '')
  );
  const userId = useSelector(selectUserId);
  const isAdmin = useSelector(isUserOrganizationAdmin);
  const { advancedRequestsCompletedStatus = false } = useFlags();
  const options = useMemo(
    () =>
      getRequestStatusOptions(
        userId,
        request,
        requestPermissions,
        value,
        isAdmin,
        advancedRequestsCompletedStatus
      ),
    [
      request,
      requestPermissions,
      userId,
      value,
      isAdmin,
      advancedRequestsCompletedStatus,
    ]
  );
  const [activeModal, setActiveModal] = useState<
    | Exclude<
        RequestStatus,
        | typeof REQUEST_STATUSES.DRAFT
        | typeof REQUEST_STATUSES.PENDING_CANCELLATION
        | typeof REQUEST_STATUSES.PENDING_PAGE_APPROVAL
      >
    | undefined
    | typeof REQUEST_STATUSES.CANCELED
  >();
  const isConfirmCancellationOpen = activeModal === REQUEST_STATUSES.CANCELED;

  return (
    <>
      <RequestStatusModals
        onCancel={() => setActiveModal(undefined)}
        activeModal={
          activeModal === REQUEST_STATUSES.CANCELED ? undefined : activeModal
        }
        onConfirmAction={async (params) => {
          if (!activeModal) return;
          try {
            await Promise.resolve(onSave?.(activeModal, params));
            setActiveModal(undefined);
          } catch {
            // Keep modal open on error; parent handles notification
          }
        }}
      />
      {request && (
        <ConfirmCancellationModal
          isOpen={isConfirmCancellationOpen}
          setIsOpen={(open) => {
            if (!open) setActiveModal(undefined);
          }}
          requestData={request}
          onConfirm={async (ldRemark) => {
            try {
              // Re-send the reason/details stamped on PENDING_CANCELLATION:
              // the backend shallow-merges the cancel payload and would
              // otherwise wipe them from the cancellation subobject (CGP-88).
              await Promise.resolve(
                onSave?.(REQUEST_STATUSES.CANCELED, {
                  comments: ldRemark,
                  reason: request.data?.cancellation?.reason,
                  details: request.data?.cancellation?.details,
                })
              );
              setActiveModal(undefined);
            } catch {
              // Keep modal open on error; parent handles notification
            }
          }}
        />
      )}
      <InlineDropdownTableRowCell
        value={value}
        customizedValue={customizedValue}
        options={options}
        isEditable={isEditable}
        className={`${className} min-w-170px`}
        onSave={(newValue) => value !== newValue && setActiveModal(newValue)}
        onCancel={onCancel}
        canEdit={canEdit}
        isDisabled={isDisabled}
        disabledMessage={disabledMessage}
        dataAttributePrefix='request-status'
      />
    </>
  );
};
